import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Play, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

const SPRING = { type: 'spring', stiffness: 520, damping: 26 };

/**
 * Full bio + episode list modal for a featured guest.
 * `guest` shape: { name, credentials, photo, bio, linkedin, episodes: [{title, slug, cover, listenUrl}] }
 */
const GuestBioModal = ({ guest, open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!guest) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          data-testid="guest-bio-modal"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={SPRING}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-[#EF5B5B]/30 glass-strong"
            style={{
              boxShadow: '0 0 0 1px rgba(239,91,91,0.25), 0 30px 80px -20px rgba(239,91,91,0.4)',
            }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              data-testid="guest-modal-close"
              aria-label="Close"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full glass border border-white/10 hover:border-[#EF5B5B]/60 flex items-center justify-center text-gray-300 hover:text-[#EF5B5B] smooth"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Hero */}
            <div className="relative">
              <div className="relative h-52 sm:h-60 overflow-hidden">
                <img
                  src={guest.photo}
                  alt=""
                  aria-hidden
                  className="w-full h-full object-cover scale-125 opacity-30 blur-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-[#0a0a0a]" />
              </div>
              <div className="absolute inset-x-0 -bottom-16 flex justify-center">
                <img
                  src={guest.photo}
                  alt={guest.name}
                  className="w-32 h-32 sm:w-36 sm:h-36 rounded-full object-cover border-4 border-[#0a0a0a] shadow-2xl"
                  style={{ boxShadow: '0 0 0 3px rgba(239,91,91,0.4), 0 20px 50px -10px rgba(239,91,91,0.55)' }}
                />
              </div>
            </div>

            <div className="pt-20 sm:pt-24 pb-8 sm:pb-10 px-6 sm:px-10 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">
                {guest.name}
              </h2>
              <p className="text-sm sm:text-base text-[#EF5B5B] font-semibold mb-6">
                {guest.credentials}
              </p>

              <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-2xl mx-auto whitespace-pre-line">
                {guest.bio}
              </p>

              {guest.linkedin && (
                <motion.a
                  href={guest.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guest-linkedin-link"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  transition={SPRING}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass border border-white/10 hover:border-[#0A66C2]/70 hover:text-[#0A66C2] text-sm font-semibold smooth"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </motion.a>
              )}
            </div>

            {/* Episodes */}
            {guest.episodes && guest.episodes.length > 0 && (
              <div className="px-6 sm:px-10 pb-10">
                <h3 className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-4">
                  Episodes with {guest.name.split(' ').slice(-1)[0]}
                </h3>
                <div className="grid gap-3">
                  {guest.episodes.map((ep) => (
                    <motion.div
                      key={ep.slug}
                      whileHover={{ x: 4 }}
                      transition={SPRING}
                      className="group flex items-center gap-4 p-3 rounded-2xl glass border border-white/5 hover:border-[#EF5B5B]/40"
                    >
                      <img src={ep.cover} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold line-clamp-2 group-hover:text-[#EF5B5B] smooth">
                          {ep.title}
                        </p>
                      </div>
                      <Link
                        to={`/blog/${ep.slug}`}
                        data-testid={`guest-episode-${ep.slug}`}
                        onClick={onClose}
                        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 btn-red rounded-full text-xs font-bold"
                      >
                        <Play className="w-3 h-3" fill="currentColor" /> Listen
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default GuestBioModal;
